import { initializeApp } from "firebase/app";
import { getFirestore, connectFirestoreEmulator, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { getAuth, connectAuthEmulator, GoogleAuthProvider } from "firebase/auth";
import { getStorage } from "firebase/storage";
import { ADMIN_EMAIL, SELF_SERVICE_ROLES } from './config';

const firebaseConfig = {
  apiKey: "AIzaSyCI2EX7aR0ZphG36_IlUQqt0nFozedj5pI",
  authDomain: "rems-app-44205.firebaseapp.com",
  projectId: "rems-app-44205",
  storageBucket: "rems-app-44205.firebasestorage.app",
  messagingSenderId: "177600513477",
  appId: "1:177600513477:web:aed2a0572ed9a688f7b7ac",
  measurementId: "G-C1X97KV5Q4"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize services
export const db = getFirestore(app);
export const auth = getAuth(app);
export const storage = getStorage(app);
export const googleProvider = new GoogleAuthProvider();

// Dev-only: connect to local Firestore/Auth emulators for the screenshot-capture
// path (plans 02/03). CRA inlines REACT_APP_* at build time; this flag is unset in
// the Vercel prod build, so this branch is dead code there — production never
// contacts an emulator. Gated on the explicit flag (not NODE_ENV) so a normal
// `npm start` with the flag unset also never connects. try/catch mirrors the
// setPendingSignupRole guard so a failed connect can never break app init.
if (process.env.REACT_APP_USE_EMULATOR === 'true') {
  try {
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  } catch {
    // Emulator unavailable — dev capture path only; never reached in prod
  }
}

const PENDING_ROLE_KEY = 'rems-pending-signup-role';

/** Called by the signup form before auth, so ensureUserExists can apply the chosen role. */
export const setPendingSignupRole = (role) => {
  try {
    sessionStorage.setItem(PENDING_ROLE_KEY, role);
  } catch {
    // sessionStorage unavailable — signup falls back to the default role
  }
};

const consumePendingSignupRole = () => {
  try {
    const role = sessionStorage.getItem(PENDING_ROLE_KEY);
    sessionStorage.removeItem(PENDING_ROLE_KEY);
    return SELF_SERVICE_ROLES.includes(role) ? role : null;
  } catch {
    return null;
  }
};

/**
 * Ensures a Firestore user document exists for the signed-in Firebase user.
 * Doc is keyed by auth UID so security rules can do get(/users/$(request.auth.uid)).
 * The userId field is kept for backward compatibility with existing queries.
 * Returns the user document data (existing or newly created).
 */
export const ensureUserExists = async (firebaseUser) => {
  if (!firebaseUser) return null;

  const userRef = doc(db, 'users', firebaseUser.uid);
  const snapshot = await getDoc(userRef);

  if (snapshot.exists()) {
    await setDoc(userRef, { lastLoginAt: serverTimestamp() }, { merge: true });
    return { id: snapshot.id, ...snapshot.data() };
  }

  const newUser = {
    userId: firebaseUser.uid,
    email: firebaseUser.email || '',
    displayName: firebaseUser.displayName || '',
    role: firebaseUser.email === ADMIN_EMAIL
      ? 'admin'
      : (consumePendingSignupRole() || 'agent'),
    assignedProperties: [],
    assignedDeals: [],
    companyId: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastLoginAt: serverTimestamp()
  };

  await setDoc(userRef, newUser);
  return { id: firebaseUser.uid, ...newUser };
};
